import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography, CircularProgress, Stack } from '@mui/material';
import { useParams } from 'react-router-dom';
import { collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { doc, getDoc } from 'firebase/firestore';
import { store } from '../../config/firebase';
import { liveGameDocs } from '../../helpers/games';
import GameByStatusCards from '../ui/gameStatusCards/gameByStatusCards';
import GameByStatusCardPublicBasic from '../ui/gameStatusCards/gameByStatusCardPublicBasic';
import { signOutUser } from '../../services/auth';
import { useColorMode } from '../../hooks/colorModeProvider';

const PublicScoringRink = () => {
  const { eventId, greenRink, venueId } = useParams();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const snapshotSeq = useRef(0);
  const dispatch = useDispatch();

  const { mode, setMode } = useColorMode();

  useEffect(() => {
    setMode('light');
  }, [mode, setMode]);

  useEffect(() => {
    if (!eventId || !greenRink) {
      setError('Missing event ID or rink parameter');
      setLoading(false);
      return;
    }

    // Extract green and rink number from the rink parameter (e.g., "A1" -> green="A", rink="1")
    const greenMatch = greenRink.match(/^([A-Z]+)(\d+)$/);

    if (!greenMatch) {
      setError('Invalid rink format. Expected format: A1, B2, etc.');
      setLoading(false);
      return;
    }

    const [, green, rinkNumber] = greenMatch;
    const rinkAsNumber = parseInt(rinkNumber, 10);

    const gamesRef = collection(store, 'games');
    const oneHourAgo = Timestamp.fromMillis(Date.now() - 60 * 60 * 1000);

    const queryConstraints = [
      where('event', '==', eventId),
      where('green', '==', green),
      where('rink', '==', rinkAsNumber)
    ];
    if (venueId) {
      queryConstraints.push(where('venue', '==', venueId));
    }

    const q = query(gamesRef, ...queryConstraints);

    let active = true;
    const unsubscribe = onSnapshot(
      q,
      async (querySnapshot) => {
        // Venue lookups can finish out of order after two quick writes, so
        // only the newest snapshot may replace the games on screen.
        const seq = ++snapshotSeq.current;
        const gamesData = await Promise.all(
          liveGameDocs(querySnapshot).map(async (docSnap) => {
            const gameData = { id: docSnap.id, ...docSnap.data() };
            // If venue is an ID, fetch venue details
            if (gameData.venue && typeof gameData.venue === 'string') {
              try {
                const venueDoc = await getDoc(doc(store, 'venues', gameData.venue));
                if (venueDoc.exists()) {
                  gameData.venue = { id: venueDoc.id, ...venueDoc.data() };
                }
              } catch (e) {
                console.log(e);
                // Optionally handle error
              }
            }
            return gameData;
          })
        );

        if (!active || seq !== snapshotSeq.current) return;

        console.log(gamesData);

        // Filter out completed games older than 1 hour
        const filteredGames = gamesData.filter((game) => {
          // Show all games that are NOT completed
          if (game.status !== 3) return true;
          // For completed games, only show if updated within the last hour
          return game.updatedAt && game.updatedAt.toMillis() >= oneHourAgo.toMillis();
        });

        // Sort games so completed games gamesData(status = 3) appear at the end
        const sortedGames = filteredGames.sort((a, b) => {
          // Sort by round (ascending)
          if (a.round !== b.round) {
            return a.round - b.round;
          }
          // Then put completed games at the end
          if (a.status === 3 && b.status !== 3) return 1;
          if (a.status !== 3 && b.status === 3) return -1;
          return 0;
        });

        setGames(sortedGames);
        setLoading(false);
        setError(null);
      },
      (err) => {
        if (!active) return;
        console.error('Error fetching games:', err);
        setError('Failed to load games');
        setLoading(false);
      }
    );

    return () => {
      active = false;
      snapshotSeq.current += 1;
      unsubscribe();
    };
  }, [eventId, greenRink]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading games...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      justifyContent={'center'}
      alignItems={'center'}
      sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Scoring - {greenRink}
      </Typography>

      {games.length === 0 ? (
        <Typography variant="h6">No games found for rink {greenRink}</Typography>
      ) : (
        <Stack alignItems={'center'} gap={2}>
          <Typography variant="h6" gutterBottom>
            Games on {greenRink} ({games.length} game{games.length !== 1 ? 's' : ''})
          </Typography>

          {games.map((game) => (
            <GameByStatusCardPublicBasic key={game.id} game={game} />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default PublicScoringRink;
